/**
 * ProofUnlockedCard — home-feed celebration card shown once a hacker has earned
 * real proof (>=1 flag or >=1 certification). Nudges them to make that proof
 * portable: copy the README/bio badge or claim a whomi.bio dev bio.
 * Dismissible (localStorage). Read-only — pulls from the public stats endpoint.
 */
import { useEffect, useState } from "react";
import { SealCheck, ArrowSquareOut, Copy, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HIDE_KEY = "hc_proof_nudge_hide";

export default function ProofUnlockedCard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [hidden, setHidden] = useState(() => {
        try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => {
        if (hidden || !user?.username) return;
        let dead = false;
        api.get(`/u/${user.username}/public-stats`)
            .then((r) => { if (!dead) setStats(r.data); })
            .catch(() => {});
        return () => { dead = true; };
    }, [hidden, user]);

    if (hidden || !stats) return null;
    const flags = stats.flags || 0;
    const certs = stats.certifications?.count || 0;
    if (flags < 1 && certs < 1) return null;

    const badgeUrl = `${API}/u/${user.username}/badge.svg`;
    const profileUrl = `${window.location.origin}/u/${user.username}`;
    const snippet = `[![hacking.community](${badgeUrl})](${profileUrl})`;

    const hide = () => {
        try { localStorage.setItem(HIDE_KEY, "1"); } catch { /* noop */ }
        setHidden(true);
    };
    const copyBadge = async () => {
        try { await navigator.clipboard.writeText(snippet); toast.success("Proof badge markdown copied"); }
        catch { toast.error("Couldn't copy"); }
    };

    const proof = [flags > 0 && `${flags} flag${flags === 1 ? "" : "s"}`, certs > 0 && `${certs} cert${certs === 1 ? "" : "s"}`]
        .filter(Boolean).join(" · ");

    return (
        <div className="hc-card p-4 bg-cyan-500/[0.04] relative" data-testid="proof-unlocked-card">
            <button onClick={hide} data-testid="proof-unlocked-hide" title="Hide"
                    className="absolute top-3 right-3 text-slate-600 hover:text-slate-300 p-1">
                <X size={12} />
            </button>
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-sm border border-cyan-500/30 bg-cyan-500/10 flex items-center justify-center">
                    <SealCheck size={18} weight="duotone" className="text-cyan-300" />
                </div>
                <div className="min-w-0 flex-1 pr-5">
                    <div className="text-sm font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        Proof unlocked — {proof}. Now show it off.
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5 leading-relaxed">
                        Drop a live, verified badge on your GitHub README or your whomi.bio dev bio — it updates itself and links back to your profile.
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-3">
                        <button onClick={copyBadge} data-testid="proof-unlocked-copy"
                                className="text-[10px] uppercase tracking-widest font-mono bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                            <Copy size={11} /> copy proof badge
                        </button>
                        <a href="https://whomi.bio" target="_blank" rel="noopener noreferrer" data-testid="proof-unlocked-whomi"
                           className="text-[10px] uppercase tracking-widest font-mono border border-white/10 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                            claim your dev bio <ArrowSquareOut size={11} />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
